import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * Last quantity asked for each line, waiting for its turn to go to the store.
   * Keyed by line item key, so a removal that shifts line numbers doesn't send a change to the wrong item.
   * @type {Map<string, {line: number, key?: string, quantity: number}>}
   */
  #pendingChanges = new Map();
  /** Whether a request to the store is in flight. Only one at a time, so responses can't arrive out of order. */
  #requestInFlight = false;
  #debouncedSendPending = debounce(this.#sendPendingChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * Every tap is kept (per line) and sent after a short pause, so taps on two lines or taps
   * while a request is in flight are not lost.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    this.#queueChange(line, quantity, event.target);
    this.#debouncedSendPending();
  };

  /**
   * Keeps the latest quantity asked for a line until it can be sent.
   * @param {number} line - The line number shown when the change was made.
   * @param {number} quantity - The quantity.
   * @param {EventTarget | null} [source] - The element that changed, used to find the line item key.
   */
  #queueChange(line, quantity, source) {
    const row = source instanceof Element ? source.closest('tr[data-key]') : null;
    const key = row instanceof HTMLElement ? row.dataset.key : undefined;
    const id = key || `line-${line}`;

    // Re-inserting keeps the order in which the lines were last touched
    this.#pendingChanges.delete(id);
    this.#pendingChanges.set(id, { line, key, quantity });
  }

  /**
   * Sends the oldest pending change, one request at a time.
   */
  #sendPendingChange() {
    if (this.#requestInFlight) return;

    const next = this.#pendingChanges.entries().next().value;
    if (!next) return;

    const [id, change] = next;
    this.#pendingChanges.delete(id);

    const line = this.#currentLine(change);
    if (!line) return this.#sendPendingChange();

    if (change.quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      key: change.key,
      quantity: change.quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows.find((row) => row.dataset.key === change.key);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Finds the line number a pending change has now, after earlier responses may have moved lines.
   * @param {{line: number, key?: string}} change - The pending change.
   * @returns {number | undefined} The line number, or undefined when the item is no longer in the cart.
   */
  #currentLine(change) {
    if (!change.key) return change.line;

    const row = this.refs.cartItemRows.find((row) => row.dataset.key === change.key);
    const input = row?.querySelector('input[data-cart-line]');

    return input instanceof HTMLInputElement ? Number(input.dataset.cartLine) || undefined : undefined;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click event.
   */
  onLineItemRemove(line, event) {
    // Wait for the request in flight, so the removal doesn't race with it
    if (this.#requestInFlight) {
      this.#queueChange(line, 0, event?.target);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

    const templateId = this.isDrawer ? 'empty-cart-drawer-template' : 'empty-cart-template';
    const template = document.getElementById(templateId);
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The line item key, sent instead of the line when known.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#requestInFlight = true;

    // Quantity buttons stay usable: taps made meanwhile are queued and sent after this response
    if (config.action !== 'change') this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;
    let retry = false;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(key ? { id: key } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // The key was replaced by an earlier response (see #refreshKeys): retry once by line number
        if (parsedResponseText.errors && key && !config.retried) {
          retry = true;
          return;
        }

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#refreshKeys(parsedResponseText.items);

        // A newer change is waiting: drawing this response would show old quantities and prices for a moment
        if (this.#pendingChanges.size > 0) return;

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#requestInFlight = false;
        if (retry) return this.updateQuantity({ ...config, key: undefined, retried: true });
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        this.#sendPendingChange();
      });
  }

  /**
   * The store gives every line a new key on each change (discount allocations are part of it).
   * Rows that were not redrawn yet, and the changes waiting in the queue, still hold the old keys:
   * swap them for the new ones by position, so the next change goes to the right item.
   * @param {Array<{key: string}>} [items] - The cart items from the response, in line order.
   */
  #refreshKeys(items) {
    if (!items) return;

    const rows = this.refs.cartItemRows.filter((row) => row.isConnected && !row.classList.contains('removing'));
    if (rows.length !== items.length) return;

    /** @type {Map<string, string>} */
    const renamed = new Map();
    rows.forEach((row, index) => {
      const oldKey = row.dataset.key;
      const newKey = items[index]?.key;
      if (!oldKey || !newKey || oldKey === newKey) return;

      row.dataset.key = newKey;
      renamed.set(oldKey, newKey);
    });
    if (!renamed.size) return;

    // Rebuild the queue with the new keys, keeping its order
    const pending = [...this.#pendingChanges.entries()];
    this.#pendingChanges.clear();
    for (const [id, change] of pending) {
      const newKey = change.key && renamed.get(change.key);
      this.#pendingChanges.set(newKey || id, newKey ? { ...change, key: newKey } : change);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
